import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { resolve } from "./types";
/**
 * Wraps its children in a boundary element and fires `onOutsideClick` whenever the user
 * clicks (via `pointerdown`) outside that boundary. Optionally handles the Escape key.
 *
 * @example
 * <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
 *   <Dropdown />
 * </OutsideClickHandler>
 */
export const OutsideClickHandler = ({ onOutsideClick, onEscapeKey, disabled, insideRefs, ignoreClassName, as: As = "div", className, style, useCapture, children, }) => {
    const wrapperRef = useRef(null); // Inside boundary.
    useEffect(() => {
        // Skip on SSR or when disabled.
        if (disabled || typeof document === "undefined")
            return () => { };
        // Is target inside wrapper or refs?
        const isInside = (target) => {
            if (!target)
                return false;
            const nodes = [
                wrapperRef.current,
                ...(insideRefs?.map(resolve) ?? []),
            ].filter(Boolean);
            return nodes.some(n => n.contains(target));
        };
        // Handle pointerdown.
        const onPointerDown = (e) => {
            if (disabled)
                return; // Guard.
            const target = e.target;
            if (ignoreClassName && target?.closest?.(`.${ignoreClassName}`))
                return;
            if (!isInside(target))
                onOutsideClick();
        };
        // Handle Escape key.
        const onKeyDown = (e) => {
            if (disabled)
                return; // Guard.
            if (e.key === "Escape" && onEscapeKey)
                onEscapeKey();
        };
        // Register listeners.
        document.addEventListener("pointerdown", onPointerDown, {
            capture: !!useCapture,
            passive: true, // Non-blocking.
        });
        document.addEventListener("keydown", onKeyDown, { capture: !!useCapture });
        // Cleanup listeners.
        return () => {
            document.removeEventListener("pointerdown", onPointerDown, {
                capture: !!useCapture,
            });
            document.removeEventListener("keydown", onKeyDown, {
                capture: !!useCapture,
            });
        };
    }, [
        disabled,
        insideRefs,
        ignoreClassName,
        onOutsideClick,
        onEscapeKey,
        useCapture,
    ]);
    // Wrapper ref defines inside boundary.
    return (_jsx(As, { ref: wrapperRef, className: className, style: style, children: children }));
};
